import { Typography, styled } from '@mui/material';
import { numberToCurrencyString } from 'further-ui/utils';
import { Column, Row } from 'further-ui/components/Layout';
import FormField from 'further-ui/components/FormField';
import { useState } from 'react';
import ApplicationDetailForm from './ApplicationDetailForm';
import { ApplicationDetail, InvestorInformation } from '../types';
import CardsListWithPreview from 'components/new/CardsListWithPreview';
import InformationForm from './InformationForm';
import CoreInformation from './CoreInformation';
import BankDetails from './BankDetails';
import TaxInformation from './TaxInformation';
import { useLayoutContext } from 'contexts/LayoutContext';
import InvestorUsers from './InvestorUsers';
import useVehicleApplication from 'hooks/lp/data/vehicleApplication/useVehicleApplication';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import { SpecialPartner } from 'further-types/lp/investor';

const BasicDetails = styled(Row)(({ theme }) => ({
  padding: theme.spacing(0, theme.spacingSizes.sm),
}));

type Props = {
  vehicleApplicationId: string;
  vehicleId: string;
};

const InvestorDetails: React.FC<Props> = ({
  vehicleApplicationId,
  vehicleId,
}) => {
  const [canEdit] = useHasPermissions([RolePermission.LpEditCommitment]);
  const { vehicle } = useLayoutContext();

  const [editedApplicationDetail, setEditedApplicationDetail] =
    useState<ApplicationDetail | null>(null);
  const [informationExpanded, setInformationExpanded] = useState(false);
  const [activeInformationForm, setActiveInformationForm] =
    useState<InvestorInformation | null>(null);

  const { vehicleApplication, bankDetails, taxDetails, investor } =
    useVehicleApplication(vehicleId, vehicleApplicationId);

  const hasTargetCommitment = vehicleApplication.commitmentAmount > 0;

  const handleDetailsToggle = () => {
    setInformationExpanded((detailsExpanded) => !detailsExpanded);
    setActiveInformationForm(null);
  };

  const handleInformationEditStart = (
    investorInformation: InvestorInformation,
  ) => {
    setInformationExpanded(true);
    setActiveInformationForm(investorInformation);
  };

  const commitmentValueLabel = vehicleApplication.commitment
    ? 'Commitment'
    : 'Target commitment';

  return (
    <Column spacing="lg">
      <BasicDetails spacing="sm" fullWidth>
        {editedApplicationDetail ? (
          <ApplicationDetailForm
            vehicleApplicationId={vehicleApplicationId}
            vehicleId={vehicleId}
            baseCurrency={vehicle?.baseCurrency}
            detail={editedApplicationDetail}
            onClose={() => {
              setEditedApplicationDetail(null);
            }}
          />
        ) : (
          <>
            <FormField
              label={commitmentValueLabel}
              spacing="xs"
              editable={!vehicleApplication.commitment && canEdit}
              onEdit={() => {
                if (vehicleApplication.commitment) {
                  return;
                }
                setEditedApplicationDetail(ApplicationDetail.TargetCommitment);
              }}
            >
              <Typography
                variant="body2"
                color={hasTargetCommitment ? 'grey.900' : undefined}
              >
                {hasTargetCommitment
                  ? numberToCurrencyString(
                      vehicleApplication.commitmentAmount,
                      {
                        currency: vehicle?.baseCurrency,
                      },
                    )
                  : '--'}
              </Typography>
            </FormField>
            <FormField
              label="Special partner"
              spacing="xs"
              editable={canEdit}
              onEdit={() => {
                setEditedApplicationDetail(ApplicationDetail.SpecialPartner);
              }}
            >
              <Typography variant="body2" color="grey.900">
                {vehicleApplication.specialPartner ===
                SpecialPartner.GeneralPartner
                  ? 'General partner'
                  : vehicleApplication.specialPartner ===
                    SpecialPartner.BritishBusinessBank
                  ? 'British Business Bank'
                  : 'No'}
              </Typography>
            </FormField>
            <FormField label="Investor created" spacing="xs">
              <Typography
                variant="body2"
                color={investor.investorAddedDate ? 'grey.900' : undefined}
              >
                {investor.investorAddedDate?.toReadableWithTime() ?? '--'}
              </Typography>
            </FormField>
            <FormField label="Last login" spacing="xs">
              <Typography
                variant="body2"
                color={investor.lastLoginTime ? 'grey.900' : undefined}
              >
                {investor.lastLoginTime?.toReadableWithTime() ?? '--'}
              </Typography>
            </FormField>
          </>
        )}
      </BasicDetails>
      <CardsListWithPreview
        cardSize="md"
        isExpanded={informationExpanded}
        onToggle={handleDetailsToggle}
      >
        <InformationForm
          title="Core information"
          hasDetails={!!investor.addresses.length}
          isActive={activeInformationForm === InvestorInformation.Core}
          onEditStart={() => {
            handleInformationEditStart(InvestorInformation.Core);
          }}
          disabled={!canEdit}
        >
          <CoreInformation
            isActive={activeInformationForm === InvestorInformation.Core}
            vehicleId={vehicleId}
            vehicleApplicationId={vehicleApplicationId}
            onSave={() => {
              setActiveInformationForm(null);
            }}
          />
        </InformationForm>
        <InformationForm
          title="Bank information"
          hasDetails={!!bankDetails}
          isActive={activeInformationForm === InvestorInformation.BankDetails}
          onEditStart={() => {
            handleInformationEditStart(InvestorInformation.BankDetails);
          }}
          disabled={!canEdit}
        >
          <BankDetails
            investorId={investor.id}
            isActive={activeInformationForm === InvestorInformation.BankDetails}
            vehicleId={vehicleId}
            vehicleApplicationId={vehicleApplicationId}
            onSave={() => {
              setActiveInformationForm(null);
            }}
          />
        </InformationForm>
        <InformationForm
          title="Tax information"
          hasDetails={!!taxDetails}
          isActive={
            activeInformationForm === InvestorInformation.TaxInformation
          }
          onEditStart={() => {
            handleInformationEditStart(InvestorInformation.TaxInformation);
          }}
          disabled={!canEdit}
        >
          <TaxInformation
            vehicleApplicationId={vehicleApplicationId}
            vehicleId={vehicleId}
            isActive={
              activeInformationForm === InvestorInformation.TaxInformation
            }
            onSave={() => {
              setActiveInformationForm(null);
            }}
          />
        </InformationForm>
      </CardsListWithPreview>
      <InvestorUsers
        vehicleApplicationId={vehicleApplicationId}
        vehicleId={vehicleId}
      />
    </Column>
  );
};

export default InvestorDetails;
